import { Injectable } from '@angular/core';

import { parseISO } from 'date-fns';

import { Coordinate } from '../../../core/common/entities/position/coordinate.class';
import {
    BaseFieldDefinition,
    BaseNestedFieldDefinition,
    BaseNestedFieldDefinitionGuard,
    FieldType
} from '../../../core/field-definitions/entities/field-definition.entity';
import { PartialRecord } from '../../../core/records/entities/partial-record.class';
import { getAny } from '../../../core/records/helpers/deep-get-set.helper';
import { FormValue } from '../../../form-controls/entities/form-value.entity';
import {
    ComplexFieldValue,
    ComplexListFieldValue,
    ComplexMapFieldValue,
    FieldValue,
    MapFieldValue,
    MultiItemFieldValue,
    SimpleFieldValue,
    ThesaurusFieldValue
} from '../../entities/field-values/field-type-values.entity';
import { GenericTypedValue } from '../../entities/field-values/generic-typed-value.entity';
import { RecordFieldValueHelper } from '../records/record-field-value.helper';
import {
    assertBooleanOrNull,
    assertFormValueBooleanOrNull,
    assertFormValueNumberOrNull,
    assertFormValueStringOrNull,
    assertNumberOrNull,
    assertSimpleFieldValue,
    assertStringOrNull,
    assertThesaurusFieldOrNull,
    isNull,
    isRecordOfComplexTypeArray,
    isRecordOfSimpleFieldValue,
    isRecordOfSimpleFieldValueArray,
    isSimpleFieldValue,
    isString,
    isThesaurusField
} from './field-validation.helper';

type MergedFieldValueFunction = (
    def: BaseFieldDefinition,
    records: PartialRecord[]
) => FormValue<any> | FormValue<any>[];

@Injectable({ providedIn: 'root' })
export class FieldReadHelper {
    private readonly FieldReadMapping: Record<FieldType, MergedFieldValueFunction> = {
        [FieldType.SimpleField]: this.getMergedSimpleFieldValue,
        [FieldType.TextField]: this.getMergedSimpleFieldValue,
        [FieldType.HiddenField]: this.getMergedUnsupportedFieldValue,
        [FieldType.BooleanField]: this.getMergedBooleanFieldValue,
        [FieldType.EnumField]: this.getMergedEnumFieldValue,
        [FieldType.LongField]: this.getMergedLongFieldValue,
        [FieldType.FramesField]: this.getMergedFramesFieldValue,
        [FieldType.DoubleField]: this.getMergedDoubleFieldValue,
        [FieldType.TimeCodeField]: this.getMergedTimeCodeFieldValue,
        [FieldType.DateField]: this.getMergedDateFieldValue,
        [FieldType.EDTFField]: this.getMergedEDTFFieldValue,
        [FieldType.TimerField]: this.getMergedMapFieldValue,
        [FieldType.ListField]: this.getMergedListFieldValue,
        [FieldType.MultiItemField]: this.getMergedMultiItemFieldValue,
        [FieldType.PositionField]: this.getMergedMapFieldValue,
        [FieldType.GeoCoordinateField]: this.getMergedUnsupportedFieldValue,
        [FieldType.UrlField]: this.getMergedSimpleFieldValue,
        [FieldType.VectorField]: this.getMergedSimpleFieldValue,
        [FieldType.MapField]: this.getMergedMapFieldValue,
        [FieldType.ThesaurusField]: this.getMergedThesaurusFieldValue,
        [FieldType.ThesaurusUriField]: this.getMergedUnsupportedFieldValue,
        [FieldType.ThesaurusLabelField]: this.getMergedUnsupportedFieldValue,
        [FieldType.EnumMapField]: this.getMergedUnsupportedFieldValue,
        [FieldType.ComplexField]: this.getMergedMapFieldValue,
        [FieldType.ComplexListField]: this.getMergedComplexListFieldValue,
        [FieldType.ComplexMapField]: this.getMergedMapFieldValue,
        [FieldType.OrderedMultiItemField]: this.getMergedUnsupportedFieldValue,
        [FieldType.FieldDefinitionReferenceField]: this.getMergedSimpleFieldValue
    };

    constructor(private readonly recordHelper: RecordFieldValueHelper) {}

    getFieldValue(def: BaseFieldDefinition, record: PartialRecord): FieldValue {
        const value: FieldValue = this.getProperty(record, def.DottedKey);
        return value;
    }

    getMergedFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<any> | FormValue<any>[] {
        return this.FieldReadMapping[def.Type].call(this, def, records);
    }

    // #region Get Simple Field Values

    // #region Simple Field
    getMergedUnsupportedFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<null> {
        console.error(`Reading ${def.Type} field not supported`);
        return new FormValue(null);
    }
    // #endregion

    // #region Simple Field
    getSimpleFieldValue(def: BaseFieldDefinition, record: PartialRecord): string | null {
        const value: FieldValue = this.getProperty(record, def.DottedKey);
        assertStringOrNull(value);
        return value;
    }

    getMergedSimpleFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<string | null> {
        const merged: FormValue<SimpleFieldValue> = this.simpleMerge(def, records);
        assertFormValueStringOrNull(merged);
        return merged;
    }
    // #endregion

    // #region Boolean Field
    getBooleanFieldValue(def: BaseFieldDefinition, record: PartialRecord): boolean | null {
        const value: FieldValue = this.getProperty(record, def.DottedKey);
        assertBooleanOrNull(value);
        return value;
    }

    getMergedBooleanFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<boolean | null> {
        const merged: FormValue<SimpleFieldValue> = this.simpleMerge(def, records);
        assertFormValueBooleanOrNull(merged);
        return merged;
    }
    // #endregion

    // #region Long Field
    getLongFieldValue(def: BaseFieldDefinition, record: PartialRecord): number | null {
        const value: FieldValue = this.getProperty(record, def.DottedKey);
        assertNumberOrNull(value);
        return value;
    }

    getMergedLongFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<number | null> {
        const merged: FormValue<SimpleFieldValue> = this.simpleMerge(def, records);
        assertFormValueNumberOrNull(merged);
        return merged;
    }
    // #endregion

    // #region Frames Field
    getFramesFieldValue(def: BaseFieldDefinition, record: PartialRecord): number | null {
        return this.getLongFieldValue(def, record);
    }

    getMergedFramesFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<number | null> {
        return this.getMergedLongFieldValue(def, records);
    }
    // #endregion

    // #region Double Field
    getDoubleFieldValue(def: BaseFieldDefinition, record: PartialRecord): number | null {
        return this.getLongFieldValue(def, record);
    }

    getMergedDoubleFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<number | null> {
        return this.getMergedLongFieldValue(def, records);
    }
    // #endregion

    // #region Date Field
    getDateFieldValue(def: BaseFieldDefinition, record: PartialRecord): Date | null {
        const dateString: string | null = this.getSimpleFieldValue(def, record);
        const dateValue: Date | null = dateString ? parseISO(dateString) : null;
        return dateValue;
    }

    getMergedDateFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<string | null> {
        const mergedDateField: FormValue<string | null> = this.getMergedSimpleFieldValue(def, records);
        return mergedDateField;
    }
    // #endregion

    // #region TimeCode Field
    getTimeCodeFieldValue(def: BaseFieldDefinition, record: PartialRecord): string | null {
        return this.getSimpleFieldValue(def, record);
    }

    getMergedTimeCodeFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<string | null> {
        return this.getMergedSimpleFieldValue(def, records);
    }
    // #endregion

    // #region EDTF Field
    getEDTFFieldValue(def: BaseFieldDefinition, record: PartialRecord): string | null {
        return this.getSimpleFieldValue(def, record);
    }

    getMergedEDTFFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<string | null> {
        return this.getMergedSimpleFieldValue(def, records);
    }
    // #endregion

    // #region Enum Field
    getEnumFieldValue(def: BaseFieldDefinition, record: PartialRecord): string | null {
        return this.getSimpleFieldValue(def, record);
    }

    getMergedEnumFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<string | null>[] {
        const values: FieldValue[] = this.getPropertyList(records, def.DottedKey);

        const clean: (string | null)[] = values.filter(
            (value): value is string | null => isString(value) || isNull(value)
        );

        const merged: FormValue<string | null>[] = this.recordHelper
            .mergeSingleToMultiple(clean)
            .filter(formValue => isString(formValue.value));

        merged.forEach(formValue => assertStringOrNull(formValue.value));
        return merged;
    }
    // #endregion

    // #endregion

    // #region Get Complex Field Values

    // #region List Field
    getListFieldValue(def: BaseFieldDefinition, record: PartialRecord): GenericTypedValue<SimpleFieldValue[]>[] {
        return this.getMultiItemFieldValue(def, record);
    }

    getMergedListFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<SimpleFieldValue>[] {
        const multiItemValues = this.getMergedMultiItemFieldValue(def, records);

        // Unpack to simpler array since all are same type
        const listItemValues = multiItemValues.map(
            multiItemValue => new FormValue(multiItemValue.value.value, multiItemValue.state)
        );
        return listItemValues;
    }
    // #endregion

    // #region MultiItem Field
    getMultiItemFieldValue(def: BaseFieldDefinition, record: PartialRecord): GenericTypedValue<SimpleFieldValue[]>[] {
        const fieldValue: FieldValue = this.getProperty(record, def.DottedKey);

        const clean: MultiItemFieldValue | null = isRecordOfSimpleFieldValueArray(fieldValue) ? fieldValue : null;

        if (!clean || Object.keys(clean).length === 0) {
            return [];
        }

        return BaseNestedFieldDefinitionGuard(def) && def.Children
            ? def.Children?.map(child => ({
                  type: child.Key,
                  value: Object.prototype.hasOwnProperty.call(clean, child.Key)
                      ? clean[child.Key].filter((value): value is SimpleFieldValue => isSimpleFieldValue(value))
                      : []
              }))
            : [];
    }

    getMergedMultiItemFieldValue(
        def: BaseFieldDefinition,
        records: PartialRecord[]
    ): FormValue<GenericTypedValue<SimpleFieldValue>>[] {
        const values: FieldValue[] = this.getPropertyList(records, def.DottedKey);

        const clean: MultiItemFieldValue[] = values
            .map(value => (isNull(value) ? {} : value))
            .filter((value): value is MultiItemFieldValue => isRecordOfSimpleFieldValueArray(value));

        if (!clean.length) {
            return [];
        }

        const mapped: GenericTypedValue<SimpleFieldValue>[][] = clean.map(children => {
            if (!children) {
                return [];
            }

            const list: GenericTypedValue<SimpleFieldValue>[] = [];
            for (const type of Object.keys(children)) {
                children[type]
                    .filter((value): value is SimpleFieldValue => isSimpleFieldValue(value))
                    .forEach(value => {
                        const obj: GenericTypedValue<SimpleFieldValue> = { type, value };
                        list.push(obj);
                    });
            }
            return list;
        });

        return this.recordHelper.mergeList(mapped);
    }
    // #endregion

    // #region Map Field
    getMapFieldValue(
        def: BaseFieldDefinition,
        record: PartialRecord,
        childKeys: string[] | null
    ): GenericTypedValue<SimpleFieldValue>[] | null {
        const value: FieldValue = this.getProperty(record, def.DottedKey);

        const clean: MapFieldValue | null = isRecordOfSimpleFieldValue(value) ? value : null;

        if (!clean) {
            return null;
        }
        const list: GenericTypedValue<SimpleFieldValue>[] = [];

        //If childKeys are passed, display something for each key
        if (childKeys) {
            for (const type of childKeys) {
                const typevalue: SimpleFieldValue | null = clean[type] ?? '-';
                assertSimpleFieldValue(typevalue);

                const obj: GenericTypedValue<SimpleFieldValue> = { type, value: typevalue };
                list.push(obj);
            }
            return list;
        }

        //only display fields with a value
        for (const type of Object.keys(clean)) {
            const typevalue: SimpleFieldValue | null = clean[type];
            assertSimpleFieldValue(typevalue);
            if (isNull(typevalue)) {
                continue;
            }
            const obj: GenericTypedValue<SimpleFieldValue> = { type, value: typevalue };
            list.push(obj);
        }

        return list;
    }

    getMergedMapFieldValue(
        def: BaseFieldDefinition,
        records: PartialRecord[]
    ): FormValue<GenericTypedValue<SimpleFieldValue>>[] {
        const values: FieldValue[] = this.getPropertyList(records, def.DottedKey);
        const nullValues = values.map(val => (val === null ? {} : val));
        const clean: MapFieldValue[] = nullValues.filter((value): value is MapFieldValue =>
            isRecordOfSimpleFieldValue(value)
        );

        if (!clean.length) {
            return [];
        }

        const mapped: GenericTypedValue<SimpleFieldValue>[][] = clean.map(children => {
            const list: GenericTypedValue<SimpleFieldValue>[] = [];

            for (const type of Object.keys(children)) {
                const value: SimpleFieldValue | null = children[type];
                assertSimpleFieldValue(value);
                if (isNull(value)) {
                    continue;
                }
                const obj: GenericTypedValue<SimpleFieldValue> = { type, value };
                list.push(obj);
            }

            return list;
        });

        return this.recordHelper.mergeMap(mapped);
    }
    // #endregion

    // #region Position Field
    getPositionFieldValue(
        def: BaseFieldDefinition,
        record: PartialRecord
    ): GenericTypedValue<SimpleFieldValue>[] | null {
        return this.getMapFieldValue(def, record, null);
    }

    getPositionFieldCoordinateValue(def: BaseNestedFieldDefinition, record: PartialRecord): Coordinate | null {
        const geoCoordinateField: BaseNestedFieldDefinition | undefined = def.Children?.find(
            child => child.Type === FieldType.GeoCoordinateField
        );

        const position: GenericTypedValue<SimpleFieldValue>[] | null = this.getPositionFieldValue(def, record);

        if (!geoCoordinateField || !position) {
            return null;
        }

        const coordinate: SimpleFieldValue =
            position.find(typedValue => typedValue.type === geoCoordinateField.Key)?.value ?? null;

        return isString(coordinate) ? Coordinate.coordinateFromString(coordinate) : null;
    }

    getPositionFieldAddressValue(def: BaseNestedFieldDefinition, record: PartialRecord): string | null {
        const position: GenericTypedValue<SimpleFieldValue>[] | null = this.getPositionFieldValue(def, record);

        const addressValue = position?.find(typeValue => typeValue.type === 'Address')?.value ?? null;
        return isString(addressValue) ? addressValue : null;
    }
    // #endregion

    // #region Thesaurus Field
    getThesaurusFieldValue(def: BaseFieldDefinition, record: PartialRecord): ThesaurusFieldValue {
        const value: FieldValue = this.getProperty(record, def.DottedKey);
        assertThesaurusFieldOrNull(value);
        return value as ThesaurusFieldValue;
    }

    getMergedThesaurusFieldValue(
        def: BaseFieldDefinition,
        records: PartialRecord[]
    ): FormValue<ThesaurusFieldValue | null>[] {
        const values: FieldValue[] = this.getPropertyList(records, def.DottedKey);

        const clean: ThesaurusFieldValue[] = values.filter((value): value is ThesaurusFieldValue =>
            isThesaurusField(value)
        );

        return this.recordHelper.mergeSingleToMultiple(clean);
    }
    // #endregion

    // #region Complex Field
    getComplexFieldValue(def: BaseFieldDefinition, record: PartialRecord): ComplexFieldValue | null {
        const value: FieldValue = this.getProperty(record, def.DottedKey);

        const clean: ComplexFieldValue | ComplexListFieldValue | null = isRecordOfComplexTypeArray(value)
            ? value
            : null;

        if (!clean) {
            return null;
        }

        return clean;
    }

    getMergedComplexFieldValue(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<ComplexMapFieldValue>[] {
        const values: FieldValue[] = this.getPropertyList(records, def.DottedKey);

        const clean: ComplexFieldValue[] = values
            .map(value => (isNull(value) ? {} : value))
            .filter((value): value is ComplexFieldValue => !!value); // To improve: typing check

        if (!clean.length) {
            return [];
        }

        const mapped: ComplexMapFieldValue[][] = clean.map(children => {
            let list: ComplexMapFieldValue[] = [];

            if (Object.keys(children).length === 0) {
                return list;
            }

            for (const type of Object.keys(children)) {
                const childValues: FieldValue[] | null = children[type];
                const mapFieldValues: ComplexMapFieldValue[] = Array.isArray(childValues)
                    ? childValues.filter((value): value is MapFieldValue => !!value)
                    : [childValues];

                list = [...list, ...mapFieldValues];
            }

            return list;
        });

        return this.recordHelper.mergeList(mapped);
    }
    // #endregion

    // #region ComplexList Field
    getComplexListFieldValue(def: BaseFieldDefinition, record: PartialRecord): ComplexListFieldValue | null {
        const value: FieldValue = this.getComplexFieldValue(def, record);

        if (!value) {
            return null;
        }

        if (Object.keys(value).length !== 1) {
            console.warn('Not a ComplexListField');
            return null;
        }

        return value;
    }

    getMergedComplexListFieldValue(
        def: BaseFieldDefinition,
        records: PartialRecord[]
    ): FormValue<ComplexListFieldValue>[] {
        const values: FieldValue[] = this.getPropertyList(records, def.DottedKey);

        const clean: ComplexListFieldValue[] = values
            .map(value => (isNull(value) ? {} : value))
            .filter((value): value is ComplexListFieldValue => !!value); // To improve: typing check

        if (!clean.length) {
            return [];
        }

        const mapped: ComplexListFieldValue[][] = clean.map(children => {
            let list: ComplexListFieldValue[] = [];

            if (Object.keys(children).length === 0) {
                return list;
            }

            for (const type of Object.keys(children)) {
                const childValues: FieldValue[] | null = children[type];
                const mapFieldValues: ComplexListFieldValue[] = Array.isArray(childValues)
                    ? childValues.filter((value): value is ComplexListFieldValue => !!value)
                    : [];

                list = [...list, ...mapFieldValues];
            }

            return list;
        });

        return this.recordHelper.mergeList(mapped);
    }
    // #endregion

    // #endregion

    // #region Utilities
    private simpleMerge(def: BaseFieldDefinition, records: PartialRecord[]): FormValue<SimpleFieldValue> {
        const values: FieldValue[] = this.getPropertyList(records, def.DottedKey);
        const clean: SimpleFieldValue[] = values.filter((value): value is SimpleFieldValue =>
            isSimpleFieldValue(value)
        );

        const merged: FormValue<SimpleFieldValue> = this.recordHelper.mergeSingleToSingle(clean);
        return merged;
    }

    private getPropertyList(records: PartialRecord[], dottedKey: string): FieldValue[] {
        return records.map(record => this.getProperty(record, dottedKey));
    }

    private getProperty(record: PartialRecord, dottedKey: string): FieldValue {
        const propertyPath: string[] = dottedKey.split('.');
        const value: FieldValue | undefined = getAny(record.metadata, ...propertyPath);

        if (typeof value === 'undefined') {
            return null;
        }

        return value;
    }
    // #endregion
}
